import { FormControlLabel, Button, Tabs, Tab } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

import MyDialog from "~/mui/MyDialog";
import ValidTillInput from "~/mui/ValidTillInput";
import { MySwitch } from "~/mui/MySwitch";
import { checkForErrorsState, getValidTill } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';
import { apiRemoveProjectAdminMessages, apiSetProjectAdminMessages, apiUpdateProject, apiToggleProjectArchivation, apiChangeProjectDebtConsumption } from '~/providers/api';
import { IAdminAdminMessages, IAdminProject } from '~/providers/admin-interfaces';
import { IUrlParams } from "~/interfaces";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import Wysiwyg, { contentToHTML, htmlToContent, IWysiwygContent } from "~/components/Wysiwyg";
import { notificationStore } from "~/stores/notification";

import "./style.less";

const TABS = {
	CZECH: 0,
	ENGLISH: 1,
};

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
	adminMessage: boolean;
	tabInd: number;
	archived: boolean;
	allowWalletDebtConsumption: boolean;
}

interface IStateFormData {
	internalSwitch: boolean;
	validTill: string;
	bodyCs: null | IWysiwygContent;
	bodyEn: null | IWysiwygContent;
}

export type TAdminMessageType = "add" | "remove";

// chyby formulare
type TStateErrors = {[key in keyof Pick<IStateFormData, "bodyCs" | "bodyEn">]: string; };

interface IAdminProjectInternalSettingsModal {
	params: IUrlParams;
	projectData: IAdminProject;
	messageData: IAdminAdminMessages;
	onSave?: (project: IAdminProject) => void;
	onAdminMessage?: (type: TAdminMessageType, data?: IAdminAdminMessages) => void;
	onArchive?: (archived: boolean) => void;
	onDebtConsumption?: (allowWalletDebtConsumption: boolean, lastUpdate: string) => void;
	onClose?: () => void;
	onChange?: (isChanged: boolean) => void;
}

export default function AdminProjectInternalSettingsModal({
	params = null,
	projectData = null,
	messageData = null,
	onSave = () => {},
	onAdminMessage = () => {},
	onArchive = () => {},
	onDebtConsumption = () => {},
	onClose = () => {},
	onChange = () => {},
}: IAdminProjectInternalSettingsModal) {
	const { trans } = useTrans();
	const { lang, isCzech } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
		adminMessage: !!messageData,
		tabInd: TABS.CZECH,
		archived: projectData.archived,
		allowWalletDebtConsumption: projectData.allowWalletDebtConsumption,
	});
	const { state: stateFormData, updateState: updateFormData } = myUseState<IStateFormData>({
		internalSwitch: projectData.internalSwitch,
		validTill: getValidTill(projectData),
		bodyCs: messageData
			? htmlToContent(messageData.message.cs)
			: null,
		bodyEn: messageData
			? htmlToContent(messageData.message.en)
			: null,
	});
	const { state: stateError, setState: setStateError } = myUseState<TStateErrors>({
		bodyCs: "",
		bodyEn: "",
	});
	const { setNotification } = notificationStore(notificationState => ({
		setNotification: notificationState.setNotification,
	}));

	function getWasChangedState(newState: Partial<IState>): boolean {
		const newStateData = {
			...state,
			...newState,
		};

		return (newStateData.adminMessage && messageData === null)
			|| (!newStateData.adminMessage && messageData !== null)
			|| (newStateData.archived !== projectData.archived)
			|| (newStateData.allowWalletDebtConsumption !== projectData.allowWalletDebtConsumption);
	}

	function getWasChangedFormData(newState?: Partial<IStateFormData>): boolean {
		const newFormDataState = {
			...stateFormData,
			...newState,
		};

		return newFormDataState.internalSwitch !== projectData.internalSwitch
			|| newFormDataState.validTill !== getValidTill(projectData);
	}

	function getWasChangedAdminMessage(newState: Partial<IStateFormData>): boolean {
		const newFormDataState = {
			...stateFormData,
			...newState,
		};
		const hasBodyCs = newFormDataState.bodyCs && newFormDataState.bodyCs.getPlainText() !== "";
		const hasBodyEn = newFormDataState.bodyEn && newFormDataState.bodyEn.getPlainText() !== "";

		return state.adminMessage && (hasBodyCs || hasBodyEn);
	}

	function updateFormDataWithChange(formDataState: Partial<IStateFormData>, stateData?: Partial<IState>) {
		const wasChanged = getWasChangedState(stateData)
			|| getWasChangedFormData(formDataState)
			|| getWasChangedAdminMessage(formDataState);

		updateFormData(formDataState);
		updateState({
			wasChanged,
			...stateData,
		});
		onChange(wasChanged);
	}

	async function dialogOnSave() {
		let lastUpdate = projectData.lastUpdate;

		// zmena pro archivaci
		if (projectData.archived !== state.archived) {
			const msgData = await apiToggleProjectArchivation("admin", params.projectId, state.archived, lang);

			if (msgData.noErrors) {
				if (!getWasChangedFormData()) {
					onArchive(state.archived);

					return;
				}
			} else {
				updateState({
					dialogLoading: false,
					errorMsg: msgData.errors.join(", "),
				});

				return;
			}
		}

		// zmena pro konzumaci pri dluhu
		if (projectData.allowWalletDebtConsumption !== state.allowWalletDebtConsumption) {
			const debtData = await apiChangeProjectDebtConsumption(projectData.id, {
				allowWalletDebtConsumption: state.allowWalletDebtConsumption,
				lastUpdate,
			}, lang);

			if (debtData.noErrors) {
				setNotification(trans("projectSettingsPage.consumptionDebt.success"));

				if (!getWasChangedFormData()) {
					onDebtConsumption(state.allowWalletDebtConsumption, debtData.data.lastUpdate);

					return;
				}

				lastUpdate = debtData.data.lastUpdate;
			} else {
				updateState({
					dialogLoading: false,
					errorMsg: trans("projectSettingsPage.consumptionDebt.error"),
				});

				return;
			}
		}

		// vytvorime/update
		if (state.adminMessage) {
			const formData: TStateErrors = {
				bodyCs: stateFormData.bodyCs?.getPlainText() || "",
				bodyEn: stateFormData.bodyEn?.getPlainText() || "",
			};
			const errorData = checkForErrorsState<TStateErrors, TStateErrors>(formData, {
				bodyCs: { check: "string-gt5", msg: trans("projectSettingsModal.tabs.error.emptyBody") },
				bodyEn: { check: "string-gt5", msg: trans("projectSettingsModal.tabs.error.emptyBody") },
			});

			setStateError(errorData.errorsState);

			// jsou tam chyby
			if (errorData.errorsCount > 0) {
				return;
			}

			updateState({
				dialogLoading: true,
				errorMsg: "",
			});

			const msgData = await apiSetProjectAdminMessages(params.projectId, {
				message: {
					cs: contentToHTML(stateFormData.bodyCs),
					en: contentToHTML(stateFormData.bodyEn),
				},
			}, lang);

			if (msgData.noErrors) {
				if (!getWasChangedFormData()) {
					onAdminMessage("add", msgData.data);

					return;
				}
			} else {
				updateState({
					dialogLoading: false,
					errorMsg: msgData.errors.join(", "),
				});

				return;
			}
		} else if (messageData) {
			// jdeme mazat
			updateState({
				dialogLoading: true,
				errorMsg: "",
			});

			const msgData = await apiRemoveProjectAdminMessages(params.projectId, lang);

			if (msgData.noErrors) {
				if (!getWasChangedFormData()) {
					onAdminMessage("remove");

					return;
				}
			} else {
				updateState({
					dialogLoading: false,
					errorMsg: msgData.errors.join(", "),
				});

				return;
			}
		}

		const outputData = await apiUpdateProject("admin", params.projectId, {
			...projectData,
			internalSwitch: stateFormData.internalSwitch,
			validTill: stateFormData.validTill,
			lastUpdate,
		}, lang);

		if (outputData.noErrors) {
			onSave(outputData.data);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: outputData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={trans("projectSettingsModal.title")} onClose={onClose} onSave={dialogOnSave} className="adminProjectInternalSettingsModal" contentClassName="adminProjectInternalSettingsModal__content" dialogLoading={state.dialogLoading} disableSave={!state.wasChanged} errorMsg={state.errorMsg}
		confirmClose={state.wasChanged} minWidth="550px">
		<div>
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={state.allowWalletDebtConsumption} onChange={event => updateFormDataWithChange({}, { allowWalletDebtConsumption: event.target.checked })} />}
				label={trans("projectSettingsPage.consumptionDebt.switch")}
			/>
		</div>
		<div>
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={stateFormData.internalSwitch} onChange={event => updateFormDataWithChange({ internalSwitch: event.target.checked })} />}
				label={trans("createProject.internalSwitch")}
			/>
		</div>
		<div>
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={state.archived} onChange={event => updateFormDataWithChange({}, { archived: event.target.checked })} />}
				label={trans("projectSettingsPage.archiveTitle")}
			/>
		</div>
		<div className="adminProjectInternalSettingsModal__validTill">
			<ValidTillInput value={stateFormData.validTill} isCzech={isCzech} onChange={date => updateFormDataWithChange({ validTill: date.toISOString() })} />
			{ stateFormData.validTill.length > 0 && <Button color="success" variant="contained" onClick={() => updateFormDataWithChange({ validTill: "" })}>
				{ trans("unlimited") }
			</Button> }
		</div>
		<div>
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={state.adminMessage} onChange={event => updateFormDataWithChange({}, { adminMessage: event.target.checked })} />}
				label={trans("admin.message")}
			/>
		</div>
		{ state.adminMessage && <div className="adminProjectInternalSettingsModal__tabs">
			<Tabs value={ state.tabInd } onChange={(___, tabInd) => updateState({ tabInd })}>
				<Tab label={trans("projectSettingsModal.tabs.czech")} icon={stateError.bodyCs && <ErrorIcon />} iconPosition="end" />
				<Tab label={trans("projectSettingsModal.tabs.english")} icon={stateError.bodyEn && <ErrorIcon />} iconPosition="end" />
			</Tabs>
			{ state.tabInd === TABS.CZECH && <div className="adminProjectInternalSettingsModal__tabContent">
				<Wysiwyg title={trans("projectSettingsModal.tabs.bodyCs")} content={stateFormData.bodyCs} error={stateError.bodyCs} onChange={content => updateFormDataWithChange({ bodyCs: content })} />
			</div> }
			{ state.tabInd === TABS.ENGLISH && <div className="adminProjectInternalSettingsModal__tabContent">
				<Wysiwyg title={trans("projectSettingsModal.tabs.bodyEn")} content={stateFormData.bodyEn} error={stateError.bodyEn} onChange={content => updateFormDataWithChange({ bodyEn: content })} />
			</div> }
		</div> }
	</MyDialog>;
}
